const DOMI_USB_FILTERS = [
    {usbVendorId: 0x1A86, usbProductId: 0x7523}, // CH340 used by the current car kit
    {usbVendorId: 0x10C4, usbProductId: 0xEA60}, // CP210x
    {usbVendorId: 0x0403, usbProductId: 0x6001} // FT232
];

const DEFAULT_MANIFEST_PATH = 'firmware/domi-car/manifest.json';
const SERIAL_BAUD_RATE = 115200;
const WIFI_CONNECT_TIMEOUT_MS = 35000;
const CAR_HTTP_VERIFY_TIMEOUT_MS = 6000;
const MODE_SWITCH_TIMEOUT_MS = 8000;
const DEVICE_INFO_TIMEOUT_MS = 5000;

const isWebSerialSupported = () => (
    typeof navigator !== 'undefined' &&
    typeof navigator.serial !== 'undefined' &&
    (typeof window === 'undefined' || window.isSecureContext)
);

const requestDomiCarPort = () => {
    if (!isWebSerialSupported()) {
        throw new Error('WEB_SERIAL_UNAVAILABLE');
    }
    return navigator.serial.requestPort({filters: DOMI_USB_FILTERS});
};

const formatUsbId = value => (
    typeof value === 'number' ? `0x${value.toString(16).toUpperCase()
        .padStart(4, '0')}` : '—'
);

const describeSerialPort = port => {
    const info = port && typeof port.getInfo === 'function' ? port.getInfo() : {};
    return {
        usbVendorId: info.usbVendorId,
        usbProductId: info.usbProductId,
        vendorLabel: formatUsbId(info.usbVendorId),
        productLabel: formatUsbId(info.usbProductId)
    };
};

const resolveManifestUrl = () => new URL(DEFAULT_MANIFEST_PATH, document.baseURI).toString();

const validateManifest = manifest => {
    if (!manifest || manifest.device !== 'DOMI_CAR' || !Array.isArray(manifest.files) || !manifest.files.length) {
        throw new Error('INVALID_FIRMWARE_MANIFEST');
    }
    manifest.files.forEach(file => {
        if (!Number.isInteger(file.address) || file.address < 0 || typeof file.url !== 'string') {
            throw new Error('INVALID_FIRMWARE_MANIFEST');
        }
    });
    return manifest;
};

const fetchFirmwareManifest = async (manifestUrl = resolveManifestUrl(), fetchImpl = fetch) => {
    const response = await fetchImpl(manifestUrl, {cache: 'no-store'});
    if (!response.ok) throw new Error('FIRMWARE_MANIFEST_UNAVAILABLE');
    return validateManifest(await response.json());
};

const bytesToHex = bytes => Array.from(bytes).map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

const verifySha256 = async (data, expectedHash) => {
    if (!expectedHash) return;
    if (!globalThis.crypto || !globalThis.crypto.subtle) throw new Error('FIRMWARE_HASH_UNAVAILABLE');
    const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
    if (bytesToHex(new Uint8Array(digest)) !== expectedHash.toLowerCase()) {
        throw new Error('FIRMWARE_HASH_MISMATCH');
    }
};

const fetchFirmwareFiles = async (manifest, fetchImpl = fetch) => {
    const loadedFiles = [];
    for (const file of manifest.files) {
        const response = await fetchImpl(new URL(file.url, document.baseURI).toString(), {cache: 'no-store'});
        if (!response.ok) throw new Error('FIRMWARE_FILE_UNAVAILABLE');
        const data = new Uint8Array(await response.arrayBuffer());
        if (file.size && data.byteLength !== file.size) throw new Error('FIRMWARE_SIZE_MISMATCH');
        await verifySha256(data, file.sha256);
        loadedFiles.push({
            address: file.address,
            data
        });
    }
    return loadedFiles;
};

const loadEspTool = () => import('esptool-js');

const flashDomiCarFirmware = async ({
    port,
    manifest,
    files,
    onLog = () => {},
    onProgress = () => {},
    espToolLoader = loadEspTool
}) => {
    if (!port) throw new Error('SERIAL_PORT_REQUIRED');
    validateManifest(manifest);

    const firmwareFiles = files || await fetchFirmwareFiles(manifest);
    const {ESPLoader, Transport} = await espToolLoader();
    const transport = new Transport(port, false);
    const terminal = {
        clean: () => {},
        write: data => onLog(String(data)),
        writeLine: data => onLog(String(data))
    };
    const loader = new ESPLoader({
        transport,
        baudrate: manifest.baudRate || 115200,
        terminal
    });

    try {
        onLog('正在连接 ESP32 下载模式…');
        const chipName = await loader.main('default_reset');
        if (!/ESP32/i.test(chipName)) throw new Error('UNSUPPORTED_ESP_CHIP');

        onLog(`已识别芯片：${chipName}`);
        await loader.writeFlash({
            fileArray: firmwareFiles,
            flashMode: manifest.flashMode || 'dio',
            flashFreq: manifest.flashFrequency || '40m',
            flashSize: manifest.flashSize || '4MB',
            eraseAll: manifest.eraseAll !== false,
            compress: true,
            reportProgress: (fileIndex, written, total) => {
                onProgress({fileIndex, written, total});
            }
        });
        onLog('固件写入和校验完成。');
        await loader.after('hard_reset');
        return {chipName};
    } finally {
        try {
            await transport.disconnect();
        } catch (error) {
            // The port can disappear briefly while the ESP32 resets. It is already safe to continue.
        }
    }
};

const validateWifiCredentials = (ssid, password) => {
    if (!ssid || !ssid.trim()) {
        throw new Error('WIFI_SSID_REQUIRED');
    }
    if (/[\r\n\t]/.test(ssid) || /[\r\n\t]/.test(password || '')) {
        throw new Error('WIFI_INVALID_CHARACTER');
    }
    if (ssid.length > 32) {
        throw new Error('WIFI_SSID_TOO_LONG');
    }
    if ((password || '').length > 63) {
        throw new Error('WIFI_PASSWORD_TOO_LONG');
    }
};

const readDomiCarInfo = async ({port, onLog = () => {}, timeoutMs = DEVICE_INFO_TIMEOUT_MS}) => {
    if (!port) throw new Error('SERIAL_PORT_REQUIRED');

    await port.open({baudRate: SERIAL_BAUD_RATE});
    const writer = port.writable.getWriter();
    const reader = port.readable.getReader();
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    let received = '';
    let timeoutId;

    try {
        await new Promise(resolve => setTimeout(resolve, 500));
        await writer.write(encoder.encode('DOMI_INFO\n'));
        onLog('正在读取小车实际安装的固件…');

        return await Promise.race([
            (async () => {
                while (true) {
                    const {value, done} = await reader.read();
                    if (done) throw new Error('SERIAL_CONNECTION_CLOSED');
                    received += decoder.decode(value, {stream: true});
                    const lines = received.split(/\r?\n/);
                    received = lines.pop();
                    for (const line of lines) {
                        if (!line) continue;
                        onLog(line);
                        if (!line.startsWith('DOMI_INFO ')) continue;
                        const info = JSON.parse(line.substring('DOMI_INFO '.length));
                        if (info.device !== 'DOMI_CAR') throw new Error('INVALID_DEVICE_INFO');
                        return info;
                    }
                }
            })(),
            new Promise((resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error('DEVICE_INFO_TIMEOUT')), timeoutMs);
            })
        ]);
    } finally {
        clearTimeout(timeoutId);
        try {
            await reader.cancel();
        } catch (error) {
            // Ignore cancellation races while closing the serial connection.
        }
        reader.releaseLock();
        writer.releaseLock();
        try {
            await port.close();
        } catch (error) {
            // The port can already be closed if the board reset while being inspected.
        }
    }
};

const configureDomiCarWirelessMode = async ({port, mode, onLog = () => {}}) => {
    if (!port) throw new Error('SERIAL_PORT_REQUIRED');
    const normalizedMode = String(mode).trim()
        .toUpperCase();
    if (!['WIFI', 'BLUETOOTH'].includes(normalizedMode)) throw new Error('INVALID_WIRELESS_MODE');

    await port.open({baudRate: SERIAL_BAUD_RATE});
    const writer = port.writable.getWriter();
    const reader = port.readable.getReader();
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    let received = '';
    let timeoutId;

    try {
        await new Promise(resolve => setTimeout(resolve, 500));
        await writer.write(encoder.encode(`DOMI_MODE\t${normalizedMode}\n`));
        onLog(`正在切换到 ${normalizedMode === 'WIFI' ? 'Wi-Fi' : '蓝牙'} 模式…`);
        await Promise.race([
            (async () => {
                while (true) {
                    const {value, done} = await reader.read();
                    if (done) throw new Error('SERIAL_CONNECTION_CLOSED');
                    received += decoder.decode(value, {stream: true});
                    const lines = received.split(/\r?\n/);
                    received = lines.pop();
                    for (const line of lines) {
                        if (!line) continue;
                        onLog(line);
                        if (line === `DOMI_MODE_SAVED ${normalizedMode}`) return;
                        if (line.startsWith('DOMI_ERROR ')) throw new Error(line.substring(11));
                    }
                }
            })(),
            new Promise((resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error('MODE_SWITCH_TIMEOUT')), MODE_SWITCH_TIMEOUT_MS);
            })
        ]);
    } finally {
        clearTimeout(timeoutId);
        try {
            await reader.cancel();
        } catch (error) {
            // The board restarts immediately after saving the selected mode.
        }
        reader.releaseLock();
        writer.releaseLock();
        try {
            await port.close();
        } catch (error) {
            // The USB serial adapter may already be closed while the board restarts.
        }
    }
};

const clearDomiCarBluetoothBonds = async ({port, onLog = () => {}}) => {
    if (!port) throw new Error('SERIAL_PORT_REQUIRED');

    await port.open({baudRate: SERIAL_BAUD_RATE});
    const writer = port.writable.getWriter();
    const reader = port.readable.getReader();
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    let received = '';
    let timeoutId;

    try {
        await new Promise(resolve => setTimeout(resolve, 500));
        await writer.write(encoder.encode('DOMI_BT_CLEAR_BONDS\n'));
        onLog('正在清除小车保存的蓝牙配对…');
        await Promise.race([
            (async () => {
                while (true) {
                    const {value, done} = await reader.read();
                    if (done) throw new Error('SERIAL_CONNECTION_CLOSED');
                    received += decoder.decode(value, {stream: true});
                    const lines = received.split(/\r?\n/);
                    received = lines.pop();
                    for (const line of lines) {
                        if (!line) continue;
                        onLog(line);
                        if (line === 'DOMI_BT_BONDS_CLEARED') return;
                        if (line.startsWith('DOMI_ERROR ')) throw new Error(line.substring(11));
                    }
                }
            })(),
            new Promise((resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error('BLUETOOTH_CLEAR_TIMEOUT')), MODE_SWITCH_TIMEOUT_MS);
            })
        ]);
    } finally {
        clearTimeout(timeoutId);
        try {
            await reader.cancel();
        } catch (error) {
            // Ignore cancellation races while closing the serial connection.
        }
        reader.releaseLock();
        writer.releaseLock();
        try {
            await port.close();
        } catch (error) {
            // The adapter can already be closed if the board reset or disconnected.
        }
    }
};

const verifyDomiCarConnection = async (address, {
    fetchImpl = fetch,
    timeoutMs = CAR_HTTP_VERIFY_TIMEOUT_MS
} = {}) => {
    let timeoutId;
    try {
        const commandUrl = new URL('/stop', `http://${String(address).trim()}`);
        commandUrl.searchParams.set('domi', Date.now());
        await Promise.race([
            fetchImpl(commandUrl.toString(), {
                method: 'GET',
                mode: 'no-cors',
                cache: 'no-store'
            }),
            new Promise((resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error('CAR_HTTP_UNREACHABLE')), timeoutMs);
            })
        ]);
    } catch (error) {
        throw new Error('CAR_HTTP_UNREACHABLE');
    } finally {
        clearTimeout(timeoutId);
    }
};

const provisionDomiCarWifi = async ({port, ssid, password = '', onLog = () => {}}) => {
    if (!port) throw new Error('SERIAL_PORT_REQUIRED');
    validateWifiCredentials(ssid, password);

    await port.open({baudRate: SERIAL_BAUD_RATE});
    const writer = port.writable.getWriter();
    const reader = port.readable.getReader();
    const encoder = new TextEncoder();
    const decoder = new TextDecoder();
    let received = '';
    let timeoutId;

    try {
        // Give an ESP32 that has just rebooted after flashing time to initialize its serial command handler.
        await new Promise(resolve => setTimeout(resolve, 500));
        await writer.write(encoder.encode(`DOMI_WIFI\t${ssid.trim()}\t${password}\n`));
        onLog('Wi-Fi 配置已发送，正在等待小车联网…');

        return await Promise.race([
            (async () => {
                while (true) {
                    const {value, done} = await reader.read();
                    if (done) throw new Error('SERIAL_CONNECTION_CLOSED');
                    received += decoder.decode(value, {stream: true});
                    const lines = received.split(/\r?\n/);
                    received = lines.pop();
                    for (const line of lines) {
                        if (!line) continue;
                        onLog(line);
                        // ESP32 camera-server diagnostics do not always end with a newline, so the
                        // protocol marker can be appended to an SDK log line. Search within the
                        // complete line instead of requiring the marker to be at column zero.
                        const connectedMatch = line.match(/DOMI_WIFI_CONNECTED\s+(\S+)/);
                        if (connectedMatch) {
                            return {ipAddress: connectedMatch[1]};
                        }
                        if (line.startsWith('DOMI_ERROR ')) throw new Error(line.substring(11));
                    }
                }
            })(),
            new Promise((resolve, reject) => {
                timeoutId = setTimeout(() => reject(new Error('WIFI_CONNECT_TIMEOUT')), WIFI_CONNECT_TIMEOUT_MS);
            })
        ]);
    } finally {
        clearTimeout(timeoutId);
        try {
            await reader.cancel();
        } catch (error) {
            // Ignore cancellation races while the board is rebooting.
        }
        reader.releaseLock();
        writer.releaseLock();
        try {
            await port.close();
        } catch (error) {
            // The USB serial adapter may already be closed after a board reset.
        }
    }
};

export {
    clearDomiCarBluetoothBonds,
    configureDomiCarWirelessMode,
    DOMI_USB_FILTERS,
    describeSerialPort,
    fetchFirmwareFiles,
    fetchFirmwareManifest,
    flashDomiCarFirmware,
    formatUsbId,
    isWebSerialSupported,
    provisionDomiCarWifi,
    readDomiCarInfo,
    requestDomiCarPort,
    resolveManifestUrl,
    validateManifest,
    validateWifiCredentials,
    verifyDomiCarConnection,
    verifySha256
};
